import SEO from "@/components/SEO";
import { ShieldCheck, Lock, FileCheck, User, AlertCircle, Mail } from "lucide-react";

export default function ProtectionDonnees() {
  return (
    <>
      <SEO
        title="Protection des données — Politique RGPD"
        description="Politique de protection des données personnelles YAFORMIS : finalités, base légale, durées de conservation, destinataires, droits RGPD. Transparence totale."
        path="/protection-donnees"
      />

      <section className="bg-white py-12 md:py-16 border-b border-brand-line" data-testid="protection-hero">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <div className="overline">RGPD · Confidentialité</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-[1.05] text-brand-ink">
            Protection des données personnelles
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
            YAFORMIS respecte le Règlement général sur la protection des données (RGPD) et la loi française « Informatique et Libertés ». Cette page détaille comment nous collectons, utilisons et protégeons vos données.
          </p>
        </div>
      </section>

      <section className="bg-white py-10 md:py-14" data-testid="protection-content">
        <div className="max-w-3xl mx-auto px-5 md:px-8 space-y-10">
          <Block icon={User} title="Qui est responsable de vos données ?">
            <p>Le responsable du traitement des données collectées sur ce site est :</p>
            <ul className="mt-3 space-y-1.5 text-[15px]">
              <li><strong>YAFORMIS</strong></li>
              <li>77 Bis rue Pierre Brossolette, 95200 Sarcelles</li>
              <li>Email : <a href="mailto:yaformis@gmail.com" className="text-brand-blue hover:underline">yaformis@gmail.com</a></li>
              <li>Téléphone : <a href="tel:+33626345597" className="text-brand-blue hover:underline">06 26 34 55 97</a></li>
            </ul>
          </Block>

          <Block icon={FileCheck} title="Quelles données collectons-nous ?">
            <p>Nous collectons uniquement les données strictement nécessaires au traitement de votre demande :</p>
            <ul className="mt-3 space-y-2">
              <Bullet>Identité : nom, prénom, fonction, entreprise</Bullet>
              <Bullet>Coordonnées : adresse email, numéro de téléphone</Bullet>
              <Bullet>Contenu de votre message et formation visée</Bullet>
              <Bullet>Données techniques : adresse IP (uniquement pour la protection anti-spam, durée 24 h)</Bullet>
            </ul>
            <p className="mt-3">Aucune donnée sensible n'est collectée.</p>
          </Block>

          <Block icon={ShieldCheck} title="À quelles fins vos données sont-elles utilisées ?">
            <ul className="space-y-2">
              <Bullet>Répondre à votre demande d'information ou de devis</Bullet>
              <Bullet>Vous proposer un parcours de formation adapté à votre projet</Bullet>
              <Bullet>Gérer la relation commerciale et les inscriptions aux formations</Bullet>
              <Bullet>Assurer la sécurité du site (prévention des envois automatisés)</Bullet>
              <Bullet>Vous envoyer notre newsletter uniquement si vous y avez consenti</Bullet>
            </ul>
          </Block>

          <Block icon={FileCheck} title="Base légale du traitement">
            <ul className="space-y-2">
              <Bullet><b>Consentement</b> : pour la newsletter et le traitement de votre demande via le formulaire de contact.</Bullet>
              <Bullet><b>Exécution de mesures précontractuelles</b> : pour l'envoi d'un devis à votre demande.</Bullet>
              <Bullet><b>Intérêt légitime</b> : pour la sécurité du site (anti-spam).</Bullet>
              <Bullet><b>Obligation légale</b> : conservation des documents comptables et contractuels liés aux formations.</Bullet>
            </ul>
          </Block>

          <Block icon={Lock} title="Durées de conservation">
            <ul className="space-y-2">
              <Bullet><b>Prospects (formulaire de contact)</b> : 36 mois à compter du dernier contact.</Bullet>
              <Bullet><b>Clients (conventions, factures)</b> : 10 ans (obligation comptable et fiscale).</Bullet>
              <Bullet><b>Newsletter</b> : jusqu'au désabonnement, puis 30 jours en archive.</Bullet>
              <Bullet><b>Logs techniques et IP</b> : 24 heures.</Bullet>
            </ul>
          </Block>

          <Block icon={User} title="À qui vos données sont-elles communiquées ?">
            <p>Vos données ne sont jamais vendues ni cédées à des tiers à des fins commerciales. Elles peuvent être transmises uniquement à :</p>
            <ul className="mt-3 space-y-2">
              <Bullet>L'équipe pédagogique interne de YAFORMIS, pour traiter votre demande</Bullet>
              <Bullet>Nos sous-traitants techniques strictement nécessaires (hébergement cloud UE, service d'envoi d'emails) et liés par contrat de confidentialité</Bullet>
              <Bullet>Les OPCO et organismes de financement, uniquement avec votre accord dans le cadre d'une inscription</Bullet>
              <Bullet>Les autorités publiques en cas d'obligation légale</Bullet>
            </ul>
            <p className="mt-3">Aucun transfert hors Union Européenne n'est réalisé.</p>
          </Block>

          <Block icon={ShieldCheck} title="Vos droits">
            <p>Conformément au RGPD, vous disposez des droits suivants :</p>
            <ul className="mt-3 space-y-2">
              <Bullet><b>Droit d'accès</b> : obtenir une copie des données que nous détenons sur vous.</Bullet>
              <Bullet><b>Droit de rectification</b> : corriger des données inexactes.</Bullet>
              <Bullet><b>Droit à l'effacement</b> (« droit à l'oubli ») : demander la suppression de vos données.</Bullet>
              <Bullet><b>Droit à la limitation</b> : geler temporairement l'usage de vos données.</Bullet>
              <Bullet><b>Droit à la portabilité</b> : récupérer vos données dans un format structuré.</Bullet>
              <Bullet><b>Droit d'opposition</b> : refuser certains traitements.</Bullet>
              <Bullet><b>Droit de retirer votre consentement</b> à tout moment.</Bullet>
              <Bullet><b>Droit de définir des directives</b> sur le sort de vos données après votre décès.</Bullet>
            </ul>
          </Block>

          <Block icon={Mail} title="Comment exercer vos droits ?">
            <p>Pour exercer l'un de ces droits ou pour toute question sur le traitement de vos données, écrivez-nous à :</p>
            <p className="mt-3">
              <a href="mailto:yaformis@gmail.com" className="inline-flex items-center gap-2 btn-cta">
                yaformis@gmail.com
                <Mail className="w-4 h-4" />
              </a>
            </p>
            <p className="mt-4 text-sm text-slate-500">Nous nous engageons à répondre dans un délai maximum d'un mois. Une pièce justificative d'identité pourra être demandée en cas de doute raisonnable.</p>
          </Block>

          <Block icon={Lock} title="Sécurité des données">
            <p>YAFORMIS met en œuvre toutes les mesures techniques et organisationnelles appropriées pour garantir la sécurité et la confidentialité de vos données :</p>
            <ul className="mt-3 space-y-2">
              <Bullet>Chiffrement HTTPS/TLS sur l'ensemble du site</Bullet>
              <Bullet>Accès aux données limité au personnel autorisé</Bullet>
              <Bullet>Mesures anti-spam et détection d'intrusions</Bullet>
              <Bullet>Sauvegardes régulières et plans de reprise d'activité</Bullet>
            </ul>
          </Block>

          <Block icon={FileCheck} title="Cookies">
            <p>Le site utilise exclusivement des cookies techniques nécessaires à son bon fonctionnement et à la mesure d'audience anonymisée. Aucun cookie publicitaire ou de profilage n'est déposé. Aucune bannière de consentement n'est donc requise.</p>
          </Block>

          <Block icon={AlertCircle} title="Réclamation auprès de la CNIL">
            <p>
              Si vous estimez que vos droits n'ont pas été respectés, vous pouvez introduire une réclamation auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-brand-blue hover:underline">www.cnil.fr</a>.
            </p>
          </Block>

          <div className="pt-6 border-t border-brand-line text-sm text-slate-500">
            <p>Politique de protection des données mise à jour le 1<sup>er</sup> décembre 2025.</p>
          </div>
        </div>
      </section>
    </>
  );
}

function Block({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 bg-brand-page border border-brand-line flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-brand-blue" strokeWidth={1.8} />
        </div>
        <h2 className="font-heading font-bold text-2xl text-brand-ink leading-tight pt-1">{title}</h2>
      </div>
      <div className="pl-12 space-y-2 text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="text-brand-blue font-bold mt-1 shrink-0">·</span>
      <span>{children}</span>
    </li>
  );
}
